import { useState } from 'react';
import { ApiError } from '../lib/api';
import { useI18n, useLocalized } from '../lib/i18n';
import { useApiError } from '../lib/useApiError';
import { parseMoneyToTetri } from '../lib/money';
import { useCreateCategory, useCreateService, useServiceCategories } from '../lib/queries';
import { can, useSession } from '../lib/session';
import { Alert, Button, Card, Field, MoneyInput, SectionTitle, Select } from './ui';

const EMPTY = { ka: '', ru: '', en: '' };

/**
 * Adding a service without going near the database.
 *
 * All three languages are required because the name is what a customer reads
 * on their receipt and in their account, and a service that exists only in
 * Russian leaves a blank line for everyone else.
 */
export function NewService({ onDone }: { onDone: () => void }) {
  const { t } = useI18n();
  const localized = useLocalized();
  const { data: categories } = useServiceCategories();
  const create = useCreateService();
  const createCategory = useCreateCategory();
  const errorText = useApiError();

  const [categoryId, setCategoryId] = useState('');
  const [name, setName] = useState(EMPTY);
  const [priceText, setPriceText] = useState('');
  const [followupText, setFollowupText] = useState('');
  const [payText, setPayText] = useState('');
  const { data: session } = useSession();
  const maySeePay = can(session, 'staff.pay');
  const [newCategory, setNewCategory] = useState<typeof EMPTY | null>(null);

  const error = [create.error, createCategory.error]
    .find((e): e is ApiError => e instanceof ApiError);

  const named = (value: typeof EMPTY) => value.ka.trim() && value.ru.trim() && value.en.trim();
  const ready = Boolean(categoryId) && Boolean(named(name));

  const names = (
    value: typeof EMPTY,
    set: (next: typeof EMPTY) => void,
    label: string,
  ) => (
    <div className="space-y-2">
      {(['ru', 'ka', 'en'] as const).map((code) => (
        <Field
          key={code}
          label={`${label} · ${t(`lang.${code}`)}`}
          value={value[code]}
          onChange={(event) => set({ ...value, [code]: event.target.value })}
        />
      ))}
    </div>
  );

  if (newCategory) {
    return (
      <Card>
        <SectionTitle>{t('services.newCategory')}</SectionTitle>
        {error && <div className="mb-3"><Alert>{errorText(error) ?? error.message}</Alert></div>}

        {names(newCategory, setNewCategory, t('services.categoryName'))}

        <div className="mt-4 flex gap-2">
          <Button
            className="flex-1"
            disabled={!named(newCategory)}
            loading={createCategory.isPending}
            onClick={() => createCategory.mutate({ name: newCategory }, {
              onSuccess: (category) => { setCategoryId(category.id); setNewCategory(null); },
            })}
          >
            {t('common.save')}
          </Button>
          <Button variant="ghost" onClick={() => setNewCategory(null)}>{t('common.cancel')}</Button>
        </div>
      </Card>
    );
  }

  return (
    <Card>
      <SectionTitle>{t('services.new')}</SectionTitle>
      {error && <div className="mb-3"><Alert>{errorText(error) ?? error.message}</Alert></div>}

      <div className="space-y-3">
        <div className="flex items-end gap-2">
          <div className="min-w-0 flex-1">
            <Select
              label={t('services.category')}
              value={categoryId}
              onChange={(event) => setCategoryId(event.target.value)}
            >
              <option value="">{t('services.pickCategory')}</option>
              {(categories?.items ?? []).map((category) => (
                <option key={category.id} value={category.id}>{localized(category.name)}</option>
              ))}
            </Select>
          </div>
          <Button variant="ghost" onClick={() => setNewCategory(EMPTY)}>
            {t('services.addCategory')}
          </Button>
        </div>

        {names(name, setName, t('services.name'))}

        <MoneyInput
          label={t('services.priceLabel')}
          value={priceText}
          onChange={(event) => setPriceText(event.target.value)}
        />

        {maySeePay && (
          <MoneyInput
            label={t('services.staffPay')}
            value={payText}
            onChange={(event) => setPayText(event.target.value)}
          />
        )}

        <Field
          label={t('services.followup')}
          inputMode="numeric"
          value={followupText}
          onChange={(event) => setFollowupText(event.target.value.replace(/\D/g, ''))}
        />
      </div>

      <div className="mt-4 flex gap-2">
        <Button
          className="flex-1"
          disabled={!ready}
          loading={create.isPending}
          onClick={() => create.mutate({
            categoryId,
            name: { ka: name.ka.trim(), ru: name.ru.trim(), en: name.en.trim() },
            priceFromTetri: priceText.trim() ? parseMoneyToTetri(priceText) : null,
            followupDays: followupText ? Number(followupText) : null,
            ...(maySeePay && payText.trim() ? { staffPayTetri: parseMoneyToTetri(payText) } : {}),
          }, { onSuccess: onDone })}
        >
          {t('services.add')}
        </Button>
        <Button variant="ghost" onClick={onDone}>{t('common.cancel')}</Button>
      </div>
    </Card>
  );
}
